import React, { useState, useEffect, useRef } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import FadeInSection from "../../Components/FadeInSection.jsx";
import Header from "../../Components/Header.jsx";
import Footer from "../../Components/Footer.jsx";
import SearchBar from "./SearchBar.jsx";
import { siteSettings } from "../../Data/axios";

const Verification = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [otpPopup, setOtpPopup] = useState(false);
  const [otpFieldVisible, setOtpFieldVisible] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [ownerData, setOwnerData] = useState({
    name: "",
    location: "",
    phone: "",
    userID: "",
  });
  const [page, setPage] = useState(1); // Current page for pagination
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null); // Ref for the loader
  const [filters, setFilters] = useState({}); // new state for filters
  const [totalPages, setTotalPages] = useState(0); // Total number of pages

  const handleSearchWithFilters = (appliedFilters) => {
    console.log("Applied Filters:", appliedFilters);
    setFilters(appliedFilters);
    setDrivers([]); // Clear current drivers
    setPage(1); // Reset pagination
    fetchDrivers(1, appliedFilters); // Fetch page 1 with filters
  };

  const fetchDrivers = async (pageNumber = 1, filterParams = filters) => {
    setLoading(true);
    try {
      const response = await siteSettings.getDriversForWeb(
        pageNumber,
        4,
        filterParams
      );
      setDrivers((prev) =>
        pageNumber === 1
          ? response.data || []
          : [...prev, ...(response.data || [])]
      );
      // if (totalPages === 0) {
        setTotalPages(Math.ceil(response.total / 4)); // Calculate total pages based on total drivers and items per page
      // }
    } catch (err) {
      console.log(err.message || "Failed to fetch drivers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          const loadMore = async () => {
            if (page >= totalPages) {
              console.log("No more pages to load.");
              return;
            }
            const nextPage = page + 1;
            await fetchDrivers(nextPage, filters);
            setPage(nextPage);
          };
          loadMore();
        }
      },
      { threshold: 1.0 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loading, filters, page]);

  useEffect(() => {
    fetchDrivers(1); // Fetch the first page on component mount
  }, []); // Empty dependency array means this runs only once

  const handleVerify = () => {
    setOtpPopup(true);
  };

  const handleOtpVerification = () => {
    setIsVerified(true);
    setOtpPopup(false);
    // localStorage.setItem("userID", ownerData.userID);
    // localStorage.setItem("membership", "true");
    // localStorage.setItem("permission", "granted");
  };

  return (
    <div className="bg-gray-100 font-ubuntu">
      <Header />
      <div className="container mx-auto px-8 py-16">
        <div className="text-center mb-8">
          <h2 className="text-yellow-400 text-center text-4xl font-bold uppercase tracking-wide mb-2">
            Verification
          </h2>
          <p className="text-gray-700 text-lg font-light italic max-w-3xl mx-auto">
            Find your ideal driver.
          </p>
        </div>
        <SearchBar onSearch={handleSearchWithFilters} />

        {otpPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-[90vw] md:w-[40vw]">
              <h3 className="text-xl font-semibold mb-4">OTP Verification</h3>

              {/* Phone Number Input */}
              <input
                type="text"
                placeholder="Enter Phone Number"
                className="w-full p-3 border border-gray-300 rounded-lg mb-3"
                value={ownerData.phone}
                onChange={(e) =>
                  setOwnerData({ ...ownerData, phone: e.target.value })
                }
              />

              {/* Get OTP or Verify OTP Button */}
              {!otpFieldVisible ? (
                <button
                  className="bg-yellow-400 text-white px-6 py-3 rounded-lg w-full hover:bg-csgreendark"
                  onClick={() => setOtpFieldVisible(true)}
                >
                  Get OTP
                </button>
              ) : (
                <>
                  {/* OTP Input */}
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    className="w-full p-3 border border-gray-300 rounded-lg mb-3"
                  />
                  <button
                    className="bg-yellow-400 text-white px-6 py-3 rounded-lg w-full hover:bg-csgreendark"
                    onClick={handleOtpVerification}
                  >
                    Verify OTP
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-10">
          {drivers.map((driver, index) => (
            <FadeInSection key={index} animationType="up">
              <div className="bg-gray-100 rounded-2xl shadow-lg p-6 transition-transform transform hover:scale-105">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={driver.image}
                    alt={driver.username}
                    className="w-16 h-16 border border-gray-300"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-black flex items-center gap-2">
                      {driver.username}
                      {driver.verified && (
                        <span className="text-blue-500">
                          <i className="fas fa-check-circle"></i>
                        </span>
                      )}
                    </h3>
                    {!driver.trusted ? (
                      <div className="text-blue-500 text-sm flex items-center gap-1">
                        <i className="fas fa-shield-alt"></i>
                        <span>Trusted</span>
                      </div>
                    ) : null}

                    <p className="text-gray-500 text-sm">{driver.state}</p>
                  </div>
                </div>
                <div className="text-gray-700 mb-3 flex items-center gap-4">
                  <p className="flex items-center gap-2">
                    <span>★ {driver.rating}</span>
                  </p>
                  <p className="flex items-center gap-2 ml-3">
                    <i className="fas fa-road"></i>
                    {driver.trips}
                  </p>
                </div>
                {!isVerified && (
                  <a
                    href="https://play.google.com/store/apps/details?id=com.rptpl.owner"
                    className="bg-csyellow text-white px-6 py-3 rounded-lg hover:bg-gray-300 text-center block"
                  >
                    View Profile
                  </a>
                )}
              </div>
            </FadeInSection>
          ))}
        </div>

        {/* Loader */}
        <div ref={loaderRef} className="text-center py-4">
          {loading && <p>Loading...</p>}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Verification;
